import { useState, type ReactElement } from 'react';
import type { ManagementApiKey } from '@kirin/api-client/contracts/management-api-keys';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatAbsoluteDate, formatRelativeTime } from '@/lib/date-utils';
import {
  expiryUrgency,
  managementKeyStatus,
  type ExpiryUrgency,
} from '@/lib/management-key-utils';

interface ManagementApiKeysListProps {
  keys: ManagementApiKey[];
  isLoading: boolean;
  isError: boolean;
  /** False for support, who reads the list but cannot revoke. */
  canManage: boolean;
  onRevoke: (keyId: string) => void;
  isRevoking: boolean;
}

/** `null` dates read as "Never", not as an em dash — the column is a promise. */
function formatOrNever(value: Date | null): string {
  return value ? formatAbsoluteDate(value) : 'Never';
}

/**
 * The "Expires" value: how long until the key lapses — "in 2 days", "in 6
 * months" — or "Never".
 *
 * Relative rather than absolute because the question this column answers is
 * "how much time do I have", and a timestamp makes the reader do the
 * arithmetic. It also means the urgency is stated in words on every row, so the
 * red/orange colouring is reinforcement rather than the sole carrier
 * (WCAG 1.4.1).
 *
 * A key already past its expiry reads "3 days ago", which is both truthful and
 * useful; the Status column says "Expired" alongside it.
 */
function formatExpiry(expiresAt: Date | null): string {
  return expiresAt ? formatRelativeTime(expiresAt) : 'Never';
}

/**
 * Colour for the "Expires" value: red inside 14 days, orange inside 30, plain
 * beyond that — so a key about to lapse stands out while scanning the table,
 * without painting every row. `text-destructive` and
 * `text-orange-600 dark:text-orange-400` are the pairs this app already uses
 * for these two levels of emphasis.
 *
 * The heavier weight is on `imminent` only, so weight is a third cue that
 * separates the two tiers rather than something they share.
 */
const EXPIRY_URGENCY_CLASS: Readonly<Record<ExpiryUrgency, string>> = {
  imminent: 'text-destructive font-medium',
  approaching: 'text-orange-600 dark:text-orange-400',
  none: '',
};

/**
 * Table of an organization's management API keys (KIR-3748).
 *
 * Revoke is offered only on rows that are still `Active` — revoking an expired
 * or already-revoked key is a no-op the backend would reject anyway — and is
 * disabled outright for support.
 */
export function ManagementApiKeysList({
  keys,
  isLoading,
  isError,
  canManage,
  onRevoke,
  isRevoking,
}: ManagementApiKeysListProps): ReactElement {
  const [pendingRevokeId, setPendingRevokeId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="text-sm text-muted-foreground">
        Loading management API keys…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-sm text-destructive">
        Failed to load management API keys.
      </div>
    );
  }

  if (keys.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        No management API keys yet.
      </div>
    );
  }

  const pendingKey = keys.find(key => key.id === pendingRevokeId) ?? null;

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Prefix</TableHead>
            <TableHead>Created by</TableHead>
            <TableHead>Created</TableHead>
            <TableHead>Expires</TableHead>
            <TableHead>Last used</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {keys.map(key => {
            const status = managementKeyStatus(key);
            const urgency = expiryUrgency(key.expiresAt);
            return (
              <TableRow key={key.id}>
                <TableCell>{key.name ?? '—'}</TableCell>
                <TableCell className="font-mono text-xs">
                  {key.keyPrefix}…
                </TableCell>
                <TableCell>
                  {key.createdByName ?? key.createdByEmail ?? '—'}
                </TableCell>
                <TableCell>{formatAbsoluteDate(key.createdAt)}</TableCell>
                <TableCell
                  className={EXPIRY_URGENCY_CLASS[urgency]}
                  // The exact instant is no longer on screen, so keep it
                  // reachable on hover. Supplementary detail only — the cell's
                  // own text already carries the answer and the urgency.
                  title={
                    key.expiresAt
                      ? formatAbsoluteDate(key.expiresAt)
                      : undefined
                  }
                >
                  {formatExpiry(key.expiresAt)}
                </TableCell>
                <TableCell>{formatOrNever(key.lastUsedAt)}</TableCell>
                <TableCell>
                  <Badge
                    variant={
                      status === 'Active'
                        ? 'secondary'
                        : status === 'Expired'
                          ? 'outline'
                          : 'destructive'
                    }
                  >
                    {status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {status === 'Active' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPendingRevokeId(key.id)}
                      disabled={!canManage || isRevoking}
                      aria-label={`Revoke ${key.name ?? key.keyPrefix}`}
                    >
                      Revoke
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <ConfirmationDialog
        isOpen={pendingKey !== null}
        onClose={() => setPendingRevokeId(null)}
        onConfirm={() => {
          if (!pendingKey) return;
          onRevoke(pendingKey.id);
          setPendingRevokeId(null);
        }}
        title="Revoke this API key?"
        description={`"${pendingKey?.name ?? pendingKey?.keyPrefix ?? ''}" stops working immediately, and any integration using it will start failing. This cannot be undone.`}
        confirmLabel="Revoke"
        isLoading={isRevoking}
      />
    </>
  );
}
